import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useFloatMessage, MESSAGE_TYPE } from '../../../Providers/FloatMessage';
import { UpdateSuggestion as BLUpdateSuggestion } from '../../../../utilities/BL/Admin/Overlays/Playlist';

/**
 * MODAL DE CORRECCIÓN ORTOGRÁFICA Y EDICIÓN RÁPIDA DE SUGERENCIAS [HU-07]
 * 
 * Permite al DJ u operador corregir errores de dedo, cambiar artista o aficionado
 * y ajustar el tipo de sugerencia (Canción vs Artista).
 * 
 * @component
 * @param {Object} props
 * @param {Object|null} props.suggestionData - Datos de la sugerencia a editar.
 * @param {Function} props.onClose - Callback al cerrar el modal.
 * @param {Function} [props.onSuccess] - Callback al guardar exitosamente la corrección.
 * @param {string|null} [props.roomId=null] - Identificador GUID de la sala.
 */
export const PlaylistEditSongModal = ({
    suggestionData = null,
    onClose = () => {},
    onSuccess = () => {},
    roomId = null
}) => {
    const { showMessage } = useFloatMessage();

    // Estado del formulario de edición
    const [formData, setFormData] = useState({
        title: '',
        artistName: '',
        suggestionType: 1, // 1: Canción, 2: Artista
        fanName: ''
    });

    const [isSubmitting, setIsSubmitting] = useState(false);

    // Cargar datos iniciales de la sugerencia al montar o cambiar prop
    useEffect(() => {
        if (suggestionData) {
            setFormData({
                title: suggestionData.title || '',
                artistName: suggestionData.artistName || '',
                suggestionType: Number(suggestionData.suggestionType) || 1,
                fanName: suggestionData.fanName || suggestionData.createdByOperatorName || ''
            });
        }
    }, [suggestionData]);

    // Manejador de teclado para cerrar con ESC
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape' && !isSubmitting) {
                onClose();
            }
        };

        if (suggestionData) {
            window.addEventListener('keydown', handleKeyDown);
        }

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [suggestionData, isSubmitting, onClose]);

    if (!suggestionData) return null;

    /**
     * Envía la actualización de la sugerencia a la base de datos vía BL.
     */
    const handleSubmit = async (e) => {
        e.preventDefault();

        const cleanTitle = (formData.title || '').trim();
        const cleanArtist = (formData.artistName || '').trim();
        const cleanFan = (formData.fanName || '').trim();

        if (!cleanTitle) {
            showMessage({
                type: MESSAGE_TYPE.WARNING,
                text: 'El título de la canción o nombre del artista es obligatorio.'
            });
            return;
        }

        setIsSubmitting(true);
        try {
            const payload = {
                roomId: roomId || null,
                suggestionId: suggestionData.id,
                title: cleanTitle,
                artistName: cleanArtist,
                suggestionType: Number(formData.suggestionType) || 1,
                fanName: cleanFan
            };

            const response = await BLUpdateSuggestion(payload);

            if (response.success && response.data) {
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: `✏️ Sugerencia "${cleanTitle}" actualizada exitosamente.`
                });
                onSuccess(response.data);
                onClose();
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: response.message || 'No fue posible actualizar la sugerencia.'
                });
            }
        } catch (error) {
            console.error('[PlaylistEditSongModal] Error al actualizar sugerencia:', error);
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error de red al actualizar la pista.'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="PlaylistModal">
            {/* Backdrop */}
            <div
                className="PlaylistModal__Backdrop"
                onClick={() => !isSubmitting && onClose()}
                aria-hidden="true"
            />

            {/* Contenedor del Modal */}
            <div className="PlaylistModal__Container" role="dialog" aria-modal="true">
                {/* Encabezado */}
                <div className="PlaylistModal__Header">
                    <div className="PlaylistModal__HeaderInfo">
                        <span className="PlaylistModal__HeaderIcon">✏️</span>
                        <div>
                            <h2 className="PlaylistModal__Title">Corrección de Pista / Sugerencia</h2>
                            <span className="PlaylistModal__Subtitle">
                                Corrige faltas de ortografía o ajusta el artista e intérprete
                            </span>
                        </div>
                    </div>
                    <button
                        type="button"
                        className="PlaylistModal__CloseBtn"
                        onClick={onClose}
                        disabled={isSubmitting}
                        title="Cerrar modal (ESC)"
                    >
                        <i className="bi bi-x-lg"></i>
                    </button>
                </div>

                {/* Formulario */}
                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
                    <div className="PlaylistModal__Body">
                        {/* Título de la pista */}
                        <div className="PlaylistModal__FormGroup">
                            <label className="PlaylistModal__Label">
                                Título de la Canción o Pista <span className="text-danger">*</span>
                            </label>
                            <input
                                type="text"
                                className="PlaylistModal__Input"
                                placeholder="Título corregido..."
                                value={formData.title}
                                maxLength={200}
                                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                disabled={isSubmitting}
                                autoFocus
                                required
                            />
                        </div>

                        {/* Artista o Intérprete */}
                        <div className="PlaylistModal__FormGroup">
                            <label className="PlaylistModal__Label">
                                Artista / Banda / Intérprete
                            </label>
                            <input
                                type="text"
                                className="PlaylistModal__Input"
                                placeholder="Nombre del artista corregido..."
                                value={formData.artistName}
                                maxLength={200}
                                onChange={(e) => setFormData({ ...formData, artistName: e.target.value })}
                                disabled={isSubmitting}
                            />
                        </div>

                        {/* Tipo de Sugerencia */}
                        <div className="PlaylistModal__FormGroup">
                            <label className="PlaylistModal__Label">
                                Tipo de Petición
                            </label>
                            <select
                                className="PlaylistModal__Select"
                                value={formData.suggestionType}
                                onChange={(e) => setFormData({ ...formData, suggestionType: Number(e.target.value) })}
                                disabled={isSubmitting}
                            >
                                <option value={1}>🎵 Canción Específica</option>
                                <option value={2}>🎤 Artista / Grupo General</option>
                            </select>
                        </div>

                        {/* Nombre del Aficionado o Creador */}
                        <div className="PlaylistModal__FormGroup">
                            <label className="PlaylistModal__Label">
                                Solicitado por (Aficionado / Operador)
                            </label>
                            <input
                                type="text"
                                className="PlaylistModal__Input"
                                placeholder="Nombre del aficionado..."
                                value={formData.fanName}
                                maxLength={100}
                                onChange={(e) => setFormData({ ...formData, fanName: e.target.value })}
                                disabled={isSubmitting}
                            />
                        </div>
                    </div>

                    {/* Pie del Modal */}
                    <div className="PlaylistModal__Footer">
                        <button
                            type="button"
                            className="PlaylistModal__Btn PlaylistModal__Btn--Secondary"
                            onClick={onClose}
                            disabled={isSubmitting}
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            className="PlaylistModal__Btn PlaylistModal__Btn--Cyan"
                            disabled={isSubmitting || !formData.title.trim()}
                        >
                            {isSubmitting ? (
                                <>
                                    <span className="PlaylistModal__Spinner"></span>
                                    <span>Guardando...</span>
                                </>
                            ) : (
                                <>
                                    <i className="bi bi-check-circle-fill"></i>
                                    <span>Guardar Corrección</span>
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

PlaylistEditSongModal.propTypes = {
    suggestionData: PropTypes.object,
    onClose: PropTypes.func.isRequired,
    onSuccess: PropTypes.func,
    roomId: PropTypes.string
};

export default PlaylistEditSongModal;
